import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { BgColorOptions, ColorOptions, UndefinedOption } from '@df/ui/atomic/atomic-options';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { PageComponentRangeSquaresImageComponent } from './range-squares-image.component';
export interface IPageComponentRangeSquaresComponentData extends NICmsContentComponent.IData {
    elements: IRangeSquaresImage[];
    variant: 'squares_row' | 'large_square_right' | 'large_square_left';
}
export interface IRangeSquaresImage {
    image?: string;
    title?: string;
    image_link?: string;
    font_color?: string;
    cta_link?: string;
    cta_copy?: string;
    hover_copy?: string;
    hover_cta?: IHoverCTA[];
    pill_copy?: string;
    pill_text_color?: string;
    pill_bg_color?: string;
}
export interface IHoverCTA {
    hover_cta_link?: string;
    hover_cta_copy?: string;
}
@Component({
    selector: 'page-component-range-squares',
    templateUrl: 'range-squares.html',
    styleUrls: ['range-squares.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, PageComponentRangeSquaresImageComponent, GridModule]
})
@ICmsComponent<PageComponentRangeSquaresComponent>({
    id: 'range-squares',
    name: 'Range Squares',
    tags: ['CLP', 'Range', 'Carousel'],
    fields: [
        <NICmsContentField.IChoiceField>{
            key: 'variant',
            type: NICmsContentField.EFieldType.choice,
            title: 'Variant',
            required: true,
            options: [
                UndefinedOption,
                { key: 'squares_row', title: 'Squares in a row' },
                { key: 'large_square_right', title: 'Large square right' },
                { key: 'large_square_left', title: 'Large square left' }
            ]
        },
        <NICmsContentField.IRepeaterField>{
            key: 'elements',
            title: 'CTA Elements',
            type: NICmsContentField.EFieldType.repeater,
            itemName: 'Item',
            defaultSize: 3,
            maxSize: 3,
            fields: [
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'title',
                    title: 'Title'
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'cta_copy',
                    title: 'CTA copy'
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'cta_link',
                    title: 'CTA link'
                },
                {
                    type: NICmsContentField.EFieldType.image,
                    key: 'image',
                    title: 'Image'
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'image_link',
                    title: 'Image link'
                },
                <NICmsContentField.IColorPickerField>{
                    key: 'font_color',
                    type: NICmsContentField.EFieldType.colorPicker,
                    title: 'Font Colour',
                    options: ColorOptions,
                    colorPreview: true
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'copy',
                    title: 'Copy'
                },
                {
                    type: NICmsContentField.EFieldType.textarea,
                    key: 'hover_copy',
                    title: 'Hover Copy'
                },
                <NICmsContentField.IField>{
                    type: NICmsContentField.EFieldType.text,
                    key: 'pill_copy',
                    title: 'Pill Copy',
                    default: 'Sale'
                },
                <NICmsContentField.IChoiceField>{
                    type: NICmsContentField.EFieldType.choice,
                    key: 'pill_text_color',
                    title: 'Pill Font Colour',
                    options: ColorOptions,
                    colorPreview: true,
                    default: 'col-w'
                },
                <NICmsContentField.IChoiceField>{
                    type: NICmsContentField.EFieldType.choice,
                    key: 'pill_bg_color',
                    title: 'Pill Background Colour',
                    options: BgColorOptions,
                    colorPreview: true,
                    default: 'bg-col-2'
                },
                <NICmsContentField.IRepeaterField>{
                    key: 'hover_cta',
                    title: 'Hover CTA',
                    type: NICmsContentField.EFieldType.repeater,
                    itemName: 'Item',
                    maxSize: 3,
                    fields: [
                        {
                            type: NICmsContentField.EFieldType.text,
                            key: 'hover_cta_copy',
                            title: 'Hover CTA copy'
                        },
                        {
                            type: NICmsContentField.EFieldType.text,
                            key: 'hover_cta_link',
                            title: 'Hover CTA link'
                        }
                    ]
                }
            ]
        }
    ]
})
export class PageComponentRangeSquaresComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'component'
    })
    component?: IPageComponentRangeSquaresComponentData;
    rangeSquares(isLarge = false): IRangeSquaresImage[] | undefined {
        if (this.component?.variant === 'large_square_right') {
            return isLarge ? this.component.elements.slice(2, 3) : this.component.elements.slice(0, 2);
        }
        if (this.component?.variant === 'large_square_left') {
            return isLarge ? this.component.elements.slice(0, 1) : this.component.elements.slice(1, 3);
        }
        return undefined;
    }
}
